import cx from 'classnames';
import * as React from 'react';

import { ICONS } from 'constants/IconConstants';
import SVGBase from 'components/SVGBase';
import SVGs from 'components/SVGs';
import './styles/Icon.scss';

export type IconColor =
  | 'currentColor'
  | 'apple'
  | 'elmer'
  | 'tuscan'
  | 'pencil'
  | 'leaf'
  | 'atlas'
  | 'pool'
  | 'ballpoint'
  | 'twilight'
  | 'mascot'
  | 'iris'
  | 'pennant'
  | 'chalkboard'
  | 'graphite'
  | 'percussion'
  | 'ruler'
  | 'paper'
  | 'whiteout'
  | 'google-sheets'
  | 'microsoft-excel'
  | 'ink'
  | 'locker'
  | 'plum-light';
export type IconName = keyof typeof ICONS | Lowercase<keyof typeof SVGs>;
export type IconSize = 'xxxlarge' | 'xxlarge' | 'xlarge' | 'large' | 'medium' | 'small' | 'inherit';

export type Props = {
  altText?: string;
  ariaHidden?: boolean;
  className?: string;
  color?: IconColor;
  name: IconName | null; // null renders an empty path node
  size?: IconSize | number;
};

/**
 * A simple svg wrapper that's capable of rendering complex svgs
 *
 * Utilize this component the same way you did in `IconDeprecated`, e.g. <Icon alt-text="Settings" name="gear" color="mascot" size="large" />
 * Add new svgs as React components to `svgs.tsx` with the appropriate viewbox & point the kebab-cased name to the new component in the `export default`
 *
 * @param {object} props
 * @param {string} props.altText: accessible description to be applied as a child of the <title> tag; use something that's as meaningful as the icon's visual description; if the icon has a label, please leave the alt text blank or screenreaders will announce the label twice
 * @param {string} props.ariaHidden: Hide this icon from screen readers; when the icon is supplemental to info that is already conveyed via text, like in `TextWithIcon`
 * @param {string} props.name: kebab-case name of the svg to render; possible names can be found in `IconConstants.ts` (`ICONS`) or `svgs.tsx` (`svgs`)
 * @param {string} props.color: colors are applied via css; this is possible because we're rendering svg html, not img tags
 * @param {string} props.size: same as above
 *
 * Unlike the older Icon component, this component allows us to add svgs with any inner html, which:
 * 1. Means we don't have to convert complex svgs to imgs, which will:
 *   a. allow preservation of a11y attributes, style changes (e.g. color) via CSS, & dynamic fills, strokes, and other svg attributes
 *   b. enable the ability to hide paths
 *   c. enable ability to animate
 *   d. other neat stuff that comes along with svg in the DOM
 * 2. Helps keep complex svg sizing & colors consistent (since `Icon` is strongly typed whereas you can skirt typing by importing an svg file & render it as an `Image`)
 * 2. Lets us utilize complex svgs in our shared components that render Icon (e.g. TextWithIcon, Avatar, etc.)
 * 3. Makes us capable of simply exporting icons to svg ourselves as devs
 */
export default function Icon({
  className = '',
  color = 'currentColor',
  size = 'medium',
  ...props
}: Props) {
  const classNames = cx(
    'Icon',
    {
      [`Icon--${color}`]: color,
      [`Icon--name-${props.name}`]: props.name,
      [`Icon--${size}`]: size,
    },
    className
  );

  const SVG = SVGs[props.name as keyof typeof SVGs];
  if (SVG) {
    return <SVG {...props} className={classNames} color={color} size={size} />;
  } else if (!props.name || ICONS[props.name as keyof typeof ICONS]) {
    return <SVGBase {...props} className={classNames} color={color} size={size} />;
  }

  return null;
}
